import Particle from '../entities/Particle.js';

/**
 * ParticlePool类 - 对象池，复用粒子对象
 * 
 * 职责:
 * - 预创建粒子对象
 * - 提供获取和释放接口
 * - 动态扩展池大小
 * - 减少GC压力
 * 
 * 实现细节:
 * - 初始池大小：5000个粒子
 * - 最大池大小：10000个粒子
 * - 使用数组管理空闲和活跃粒子
 * - 粒子重置时清除所有状态
 * 
 * 验证需求: Requirements 7.5
 */
class ParticlePool {
  constructor(initialSize = 5000) {
    this.initialSize = initialSize;
    this.maxSize = 10000;
    
    // 空闲粒子池
    this.availableParticles = [];
    
    // 活跃粒子列表
    this.activeParticles = [];
    
    // 预创建粒子
    this._createParticles(initialSize);
  }

  /**
   * 创建指定数量的粒子
   * @param {number} count - 要创建的粒子数量
   * @private
   */
  _createParticles(count) {
    for (let i = 0; i < count; i++) {
      const particle = new Particle();
      this.availableParticles.push(particle);
    }
  }

  /**
   * 获取一个粒子
   * @returns {Particle|null} 粒子对象，如果池耗尽则返回null
   */
  acquire() {
    let particle = null;
    
    // 从空闲池获取粒子
    if (this.availableParticles.length > 0) {
      particle = this.availableParticles.pop();
    } else {
      // 池耗尽，尝试扩展
      const currentTotal = this.activeParticles.length + this.availableParticles.length;
      
      if (currentTotal < this.maxSize) {
        // 扩展池（每次扩展500个）
        const expandCount = Math.min(500, this.maxSize - currentTotal);
        console.warn(`Particle pool expanding by ${expandCount} particles`);
        this._createParticles(expandCount);
        
        // 再次尝试获取
        if (this.availableParticles.length > 0) {
          particle = this.availableParticles.pop();
        }
      } else {
        // 达到最大限制
        console.warn('Particle pool exhausted, max size reached');
        return null;
      }
    }
    
    // 添加到活跃列表
    if (particle) {
      this.activeParticles.push(particle);
    }
    
    return particle;
  }

  /**
   * 释放一个粒子回池中
   * @param {Particle} particle - 要释放的粒子
   */
  release(particle) {
    if (!particle) return;
    
    // 从活跃列表移除
    const index = this.activeParticles.indexOf(particle);
    if (index !== -1) {
      this.activeParticles.splice(index, 1);
    }
    
    // 重置粒子状态
    particle.reset();
    
    // 添加回空闲池
    this.availableParticles.push(particle);
  }

  /**
   * 批量释放粒子
   * @param {Particle[]} particles - 要释放的粒子数组
   */
  releaseMultiple(particles) {
    if (!particles || particles.length === 0) return;
    
    for (const particle of particles) {
      this.release(particle);
    }
  }

  /**
   * 获取可用粒子数量
   * @returns {number}
   */
  getAvailableCount() {
    return this.availableParticles.length;
  }

  /**
   * 获取活跃粒子数量
   * @returns {number}
   */
  getActiveCount() {
    return this.activeParticles.length;
  }

  /**
   * 获取总粒子数量
   * @returns {number}
   */
  getTotalCount() {
    return this.availableParticles.length + this.activeParticles.length;
  }

  /**
   * 扩展池大小
   * @param {number} count - 要扩展的粒子数量
   * @returns {boolean} 是否成功扩展
   */
  expand(count) {
    const currentTotal = this.getTotalCount();
    
    if (currentTotal + count > this.maxSize) {
      console.warn(`Cannot expand pool: would exceed max size (${this.maxSize})`);
      return false;
    }
    
    this._createParticles(count);
    return true;
  }

  /**
   * 清理所有活跃粒子
   */
  clear() {
    // 释放所有活跃粒子
    const activeParticlesCopy = [...this.activeParticles];
    for (const particle of activeParticlesCopy) {
      this.release(particle);
    }
  }

  /**
   * 强制清理最老的粒子
   * @param {number} count - 要清理的粒子数量
   */
  forceCleanupOldest(count) {
    // 按年龄排序（最老的在前）
    const sorted = [...this.activeParticles].sort((a, b) => b.age - a.age);
    
    // 释放最老的粒子
    const toRelease = sorted.slice(0, Math.min(count, sorted.length));
    this.releaseMultiple(toRelease);
  }

  /**
   * 获取池的统计信息
   * @returns {Object}
   */
  getStats() {
    return {
      available: this.getAvailableCount(),
      active: this.getActiveCount(),
      total: this.getTotalCount(),
      maxSize: this.maxSize,
      utilizationPercent: (this.getActiveCount() / this.maxSize * 100).toFixed(1)
    };
  }
}

export default ParticlePool;
